import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useCategories, useInfiniteProducts, usePriceRange } from "../features/catalog/api";
import ProductCard from "../features/catalog/ProductCard";
import SCSpinner from "../components/loaders/SCSpinner";
import ErrorState from "../components/ui/ErrorState";
import Breadcrumbs from "../components/ui/Breadcrumbs";
import PriceRangeSlider from "../components/ui/PriceRangeSlider";
import useInfiniteScrollTrigger from "../hooks/useInfiniteScrollTrigger";
import useSEO from "../hooks/useSEO";

const SORT_OPTIONS = [
  { value: "", label: "Featured" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
  { value: "rating", label: "Top Rated" },
  { value: "newest", label: "Newest" },
];

export default function CategoryProducts() {
  const { slug } = useParams();
  const { data: categories } = useCategories();
  const [sort, setSort] = useState("");
  const [inStock, setInStock] = useState(false);
  const { data: priceBounds } = usePriceRange(slug);
  const [priceRange, setPriceRange] = useState(null);

  // Re-anchor the selected range to the new category's actual bounds
  // whenever the bounds change (category switch, or first load) rather
  // than only once — otherwise a filter picked up in one category would
  // silently carry over and clip products in the next.
  useEffect(() => {
    if (priceBounds) setPriceRange([priceBounds.min, priceBounds.max]);
  }, [priceBounds]);

  const [minPrice, maxPrice] = priceRange ?? [];
  const isPriceFiltered = priceBounds && priceRange && (priceRange[0] > priceBounds.min || priceRange[1] < priceBounds.max);

  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteProducts({
    category: slug,
    sort: sort || undefined,
    inStock: inStock || undefined,
    minPrice: isPriceFiltered ? minPrice : undefined,
    maxPrice: isPriceFiltered ? maxPrice : undefined,
  });
  const products = data?.pages.flatMap((page) => page.products) ?? [];
  const sentinelRef = useInfiniteScrollTrigger(() => fetchNextPage(), { enabled: !!hasNextPage });
  const category = categories?.find((c) => c.slug === slug);
  useSEO({
    title: category?.name,
    description: category ? `Shop ${category.name} — premium smart electrical fittings and home automation.` : undefined,
  });

  return (
    <div className="px-4 py-6">
      <Breadcrumbs items={[{ label: category?.name || "Products" }]} className="mb-3" />
      <h1 className="mb-4 font-serif text-2xl text-ink">{category?.name || "Products"}</h1>

      <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-3">
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="admin-input w-auto py-2 text-xs">
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-ink-light">
          <input
            type="checkbox"
            checked={inStock}
            onChange={(e) => setInStock(e.target.checked)}
            className="h-3.5 w-3.5 rounded accent-gold-500"
          />
          In stock only
        </label>
        {priceBounds && priceBounds.max > priceBounds.min && (
          <div className="w-full sm:w-64">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-ink-light">Price</span>
            <PriceRangeSlider
              min={priceBounds.min}
              max={priceBounds.max}
              value={priceRange}
              onChangeEnd={setPriceRange}
            />
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <SCSpinner size={56} />
        </div>
      ) : isError ? (
        <ErrorState message="Couldn't load products." onRetry={refetch} />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 lg:grid-cols-4">
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
          {/* Sentinel — scrolling this into view loads the next page.
              rootMargin on the observer gives it a head start so the
              next page arrives before the user hits the true bottom. */}
          {hasNextPage && (
            <div ref={sentinelRef} className="flex justify-center py-8">
              {isFetchingNextPage && <SCSpinner size={32} />}
            </div>
          )}
        </>
      )}

      {!isLoading && !isError && products.length === 0 && (
        <p className="mt-10 text-center text-sm text-ink-light">No products in this category yet.</p>
      )}
    </div>
  );
}
